import { IPaymentContract } from "@features/payment/ui";
import { ModelContract, ModuleDescription } from "@features/shared/module-description";
import { IRouterContract } from "@features/shared/router";
import { IUserAuthContract } from "@features/student/auth/model";
import { createEvent, createStore, sample } from "effector";
import { checkUpsellPurchased, UpsellStatus } from "@features/helpers";
import { IUpsellContract } from "../model";
import { routePathNameChanged } from "@features/app/model";
import { interval, reset } from "patronum";
import { ISubscriptionContract } from "@features/subscription/model";
import { IAppRouteUnitsContract } from "@features/app/route-units";
import { IUpsellUIContract } from ".";

const TARGET_IDS = new Set(["claude_work_bundle", "claude_work_bundle_sale"]);

const buyButtonClicked = createEvent();
const buyButtonDisabled = createEvent();
const buyButtonReset = createEvent();
const discountModalOpened = createEvent();
const discountModalClosed = createEvent();
const pollingStarted = createEvent();
const pollingStopped = createEvent();

const $isDiscountModalOpen = createStore(false)
    .on(discountModalOpened, () => true)
    .on(discountModalClosed, () => false);
const $upsellIds = createStore<string[]>([]);
const $isBuyButtonDisabled = createStore(true)
    .on(buyButtonClicked, () => true)
    .on(buyButtonDisabled, () => true)
    .on(buyButtonReset, () => false);

const { tick } = interval({
    timeout: 10000,
    start: pollingStarted,
    stop: pollingStopped,
    leading: false,
    trailing: false
});

const $claudeWorkBundleStatus = createStore<UpsellStatus | null>(null);
const checkRequested = createEvent();
const checkFinished = createEvent();
const $isCheckRequested = createStore(false).on(checkRequested, () => true);
const $isCheckPending = createStore(false)
    .on(checkRequested, () => true)
    .on(checkFinished, () => false);

const contract = new ModelContract(
    { $isDiscountModalOpen, $claudeWorkBundleStatus, $upsellIds, $isBuyButtonDisabled },
    { buyButtonClicked, discountModalOpened, discountModalClosed },
    {},
    {}
);

export type IClaudeWorkBundleContract = typeof contract;

export class ClaudeWorkBundleModule extends ModuleDescription<IClaudeWorkBundleContract> {
    constructor(
        public authModule: IUserAuthContract,
        public routerModule: IRouterContract,
        public paymentModule: IPaymentContract,
        public upsellModule: IUpsellContract,
        public upsellUiModule: IUpsellUIContract,
        public subscriptionsModule: ISubscriptionContract,
        public routesModule: IAppRouteUnitsContract
    ) {
        super();
    }

    getDefaultContract() {
        return contract;
    }

    run() {
        // The chain holds upsell ids for every provider (truegate plans are
        // resolved on the server now), so the check needs no catalog.
        sample({
            source: {
                upsells: this.upsellModule.stores.$upsells,
                subscriptions: this.subscriptionsModule.stores.$subscriptions,
                paymentSystem: this.subscriptionsModule.stores.$paymentSystem
            },
            filter: (s) => s.upsells.length > 0,
            fn: (s) => {
                const upsellIds = s.upsells.filter((u) => TARGET_IDS.has(u.upsellId)).map((u) => u.upsellId);
                if (upsellIds.length === 0) return upsellIds;
                const result = [...upsellIds];
                // The Complete Bundle grants the Claude pack too (the plain
                // "bundle" intentionally does not).
                if (!result.includes("complete_bundle")) {
                    result.push("complete_bundle");
                }
                return result;
            },
            target: $upsellIds
        });

        sample({
            clock: buyButtonClicked,
            target: this.upsellModule.events.upsellBought
        });

        // Skip during first-login funnel → shared pipeline emits upsellSaleModalOpened
        // when the next step is *_sale. Opening the discount popup here (not on every
        // skipButtonClicked) keeps library/profile exits on the shared redirect path.
        sample({
            clock: this.upsellUiModule.events.upsellSaleModalOpened,
            source: { currentUpsellId: this.upsellModule.stores.$currentUpsellId },
            filter: (s) => s.currentUpsellId === "claude_work_bundle",
            target: discountModalOpened
        });

        // Closing the discount popup ("Got it") moves the user to the sale offer.
        sample({
            clock: discountModalClosed,
            source: { currentUpsellId: this.upsellModule.stores.$currentUpsellId },
            filter: (s) => s.currentUpsellId === "claude_work_bundle",
            fn: () => "/upsell/claude_work_bundle_sale",
            target: this.routerModule.events.routerReplace
        });

        // Skip on the sale page is intentionally left to the shared skip
        // pipeline (features/upsell/ui/index.ts): it advances to the next
        // non-sale upsell, or — when this is the last one — leaves to home and
        // resets the first_login flag. A local routerReplace("/home") here would
        // bypass the first_login reset and get bounced back into the funnel.

        sample({
            source: {
                upsellIds: $upsellIds,
                upsellPath: this.routesModule.stores.upsell.$path,
                promptsLibraryPath: this.routesModule.stores.promptsLibrary.$path,
                promptsLibraryQuery: this.routesModule.stores.promptsLibrary.$query,
                isHomeRoute: this.routesModule.stores.home.$isActive,
                isProfileRoute: this.routesModule.stores.profile.$isActive,
                isLibraryRoute: this.routesModule.stores.library.$isActive,
                isAiToolsRoute: this.routesModule.stores.aiTools.$isActive,
                isChallengesRoute: this.routesModule.stores.challenges.$isActive,
                paymentSystem: this.subscriptionsModule.stores.$paymentSystem,
                isCheckRequested: $isCheckRequested,
                isCheckPending: $isCheckPending
            },
            filter: (s) =>
                s.upsellIds.length > 0 &&
                (s.isHomeRoute ||
                    s.isProfileRoute ||
                    // Desktop right rail shows the packs on these routes too.
                    s.isLibraryRoute ||
                    s.isAiToolsRoute ||
                    s.isChallengesRoute ||
                    (s.upsellPath !== null &&
                        (s.upsellPath.includes("claude_work_bundle") ||
                            s.upsellPath.includes("30k_prompts_complete_pack"))) ||
                    (s.promptsLibraryPath !== null && s.promptsLibraryPath.includes("claude_pack")) ||
                    s.promptsLibraryQuery.pack === "ultimate_ai") &&
                ["solidgate", "ecommpay", "apple", "truegate"].includes(s.paymentSystem) &&
                !s.isCheckRequested &&
                !s.isCheckPending,
            fn: (s) => s.upsellIds,
            target: [this.upsellModule.effects.checkUpsellPurchasedFx, checkRequested]
        });

        sample({
            clock: this.upsellModule.effects.checkUpsellPurchasedFx.done,
            source: {
                upsellIds: $upsellIds,
                paymentSystem: this.subscriptionsModule.stores.$paymentSystem,
                awaitingVerdict: this.upsellModule.stores.$upsellIdsAwaitingVerdict
            },
            filter: (s, { params }) =>
                s.upsellIds.length > 0 &&
                s.upsellIds.length === params.length &&
                s.upsellIds.every((id) => params.includes(id)) &&
                ["solidgate", "ecommpay", "apple", "truegate"].includes(s.paymentSystem),
            fn: (s, { result }) => {
                if (s.paymentSystem === "apple") return "Success";

                const statuses = s.upsellIds.map((upsellId) => checkUpsellPurchased(result, upsellId));

                if (statuses.includes("Success")) return "Success";
                if (statuses.includes("Processing")) return "Processing";
                // A just-bought pack in 3ds_verify reads as "Fail" until its bank
                // dialog is done; it is in progress — keep polling until the
                // webhook confirms instead of showing it as unpurchased.
                if (s.upsellIds.some((upsellId) => s.awaitingVerdict.includes(upsellId))) return "Processing";

                return "Fail";
            },
            target: $claudeWorkBundleStatus
        });

        sample({
            source: {
                status: $claudeWorkBundleStatus,
                isUpsellRoute: this.routesModule.stores.upsell.$isActive,
                upsellPath: this.routesModule.stores.upsell.$path,
                firstLoginFlag: this.upsellModule.stores.$firstLoginFlag
            },
            filter: (s) =>
                (s.status === "Success" || s.status === "Processing") &&
                s.isUpsellRoute &&
                s.upsellPath !== null &&
                s.upsellPath.includes("claude_work_bundle") &&
                !s.firstLoginFlag,
            target: [this.upsellUiModule.events.packAccessRedirectTriggered, buyButtonDisabled]
        });

        sample({
            source: {
                status: $claudeWorkBundleStatus,
                isUpsellRoute: this.routesModule.stores.upsell.$isActive,
                upsellPath: this.routesModule.stores.upsell.$path
            },
            filter: (s) =>
                s.status === "Fail" &&
                s.isUpsellRoute &&
                s.upsellPath !== null &&
                s.upsellPath.includes("claude_work_bundle"),
            target: buyButtonReset
        });

        sample({
            clock: $claudeWorkBundleStatus,
            source: { isUpsellRoute: this.routesModule.stores.upsell.$isActive },
            filter: (s, p) => !s.isUpsellRoute && p === "Processing",
            target: pollingStarted
        });

        sample({
            clock: $claudeWorkBundleStatus,
            filter: (status) => status === "Success" || status === "Fail",
            target: pollingStopped
        });

        sample({
            clock: this.upsellModule.effects.checkUpsellPurchasedFx.finally,
            source: { upsellIds: $upsellIds },
            filter: (s, { params }) =>
                s.upsellIds.length > 0 &&
                s.upsellIds.length === params.length &&
                s.upsellIds.every((id) => params.includes(id)),
            fn: () => undefined,
            target: checkFinished
        });

        sample({
            clock: this.upsellModule.effects.checkUpsellPurchasedFx.fail,
            source: { upsellIds: $upsellIds },
            filter: (s, { params }) =>
                s.upsellIds.length > 0 &&
                s.upsellIds.length === params.length &&
                s.upsellIds.every((id) => params.includes(id)),
            fn: () => undefined,
            target: [pollingStarted, buyButtonReset]
        });

        sample({
            clock: tick,
            source: { upsellIds: $upsellIds, isCheckPending: $isCheckPending },
            filter: (s) => s.upsellIds.length > 0 && !s.isCheckPending,
            fn: (s) => s.upsellIds,
            target: [this.upsellModule.effects.checkUpsellPurchasedFx, checkRequested]
        });

        // The status is not reset on a route change: every page inside the Claude pack read null
        // on mount and showed the full loading screen until the purchase check came back, which
        // flashed a skeleton on each step through the pack. The check still re-runs; the page keeps
        // the last verdict meanwhile.
        reset({
            clock: routePathNameChanged,
            target: [$isDiscountModalOpen, $isCheckRequested]
        });
    }
}
